import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import { useViewedPros } from '../../../contexts/ViewedProsContext';
import { generateColumnDef } from '../../../utils/dynamicColumns';
import type { FieldDefinition } from '../../../contexts/FieldDefinitionsContext';
import Toggle from '../../../components/Toggle';
import { Tooltip } from '../../../components/ui/Tooltip';
import quoteService, { type PartyAddress } from '../../../services/quote.service';
import { statusBadgeClass, keyAccountClass } from '../quoteColors';
import { laneAllInTotal } from '../laneSummary';
import type { QuoteGroup, QuoteListRow, QuoteStatus } from '../fixtures';

/** The two display modes of the quotes grid. */
export type QuotesViewMode = 'grouped' | 'flat';

/** Format the all-in total as "$1,037.00"; "—" when not computable (mirrors LaneTable). */
function formatTotal(n: number | null): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "40 HC · IMPORT" — size+equipType joined with the move type; either half may be absent. */
function equipMove(lane: QuoteListRow): string {
  const equip = [lane.size, lane.equipType].filter(Boolean).join(' ');
  return [equip, lane.moveType].filter(Boolean).join(' · ');
}

/** Base dray rate as "$850.00"; "—" when absent (matches LaneTable's "$<raw>"). */
function formatDray(raw: string | undefined): string {
  return raw ? `$${raw}` : '—';
}

/** YoY margin% ("59.47%"); "—" when null. */
function fmtYoyMargin(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}
/** YoY volume ("1,234"); "—" when null. */
function fmtYoyVolume(v: number | null | undefined): string {
  return v == null ? '—' : v.toLocaleString();
}

/** Muted recno shown after a customer / bill-to name (e.g. "ACME 9382"). Null
 *  for absent/zero recnos so nothing renders. */
function recnoTag(recno: number | null | undefined) {
  return recno ? (
    <span className="ml-1.5 text-slate-400 dark:text-slate-500 font-normal">{recno}</span>
  ) : null;
}

/** Amber "(NEW CUSTOMER)" tag for a quote whose customercode is explicitly 0 —
 *  the quote isn't linked to a cms.customers row, so the user knows to open it
 *  and pick the real customer. Null (recno unknown/absent) renders nothing. */
function newCustomerTag(recno: number | null | undefined) {
  return recno === 0 ? (
    <span className="ml-1.5 text-[0.625rem] font-bold text-amber-700 dark:text-amber-400 tracking-wide whitespace-nowrap">(NEW CUSTOMER)</span>
  ) : null;
}

// Full name + address hover tooltip for the Customer / Bill-To cells. The address
// isn't in the grid payload, so it's lazy-fetched on first hover and cached by
// (kind, id) — parties repeat heavily across rows (e.g. one bill-to per customer),
// so this is a handful of requests. undefined = loading, null = fetch failed.
const partyCache = new Map<string, PartyAddress | null>();

const PartyAddressCard: React.FC<{ kind: 'billto' | 'customer'; id: number }> = ({ kind, id }) => {
  const key = `${kind}:${id}`;
  const [data, setData] = useState<PartyAddress | null | undefined>(partyCache.get(key));
  useEffect(() => {
    if (partyCache.has(key)) { setData(partyCache.get(key)); return; }
    let cancelled = false;
    quoteService.partyAddress(kind, id)
      .then((d) => { partyCache.set(key, d); if (!cancelled) setData(d); })
      .catch(() => { partyCache.set(key, null); if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [key]);

  if (data === undefined) return <div className="text-[0.75rem] text-slate-300">Loading…</div>;
  if (data === null) return <div className="text-[0.75rem] text-slate-300">No details available.</div>;
  const cityLine = [data.city, data.state].filter(Boolean).join(', ') + (data.zip ? ` ${data.zip}` : '');
  return (
    <div className="text-left text-[0.75rem] leading-snug max-w-[260px]">
      <div className="font-semibold">{data.name || '—'}</div>
      {data.address1 && <div>{data.address1}</div>}
      {data.address2 && <div>{data.address2}</div>}
      {data.address3 && <div>{data.address3}</div>}
      {cityLine.trim() && <div>{cityLine.trim()}</div>}
      {data.sageTag && <div className="mt-1 text-slate-400">Sage: {data.sageTag}</div>}
    </div>
  );
};

/** Wrap a Customer/Bill-To cell node in the address tooltip. No tooltip for a
 *  missing/zero id (e.g. a deferred "(NEW BILL TO)" that has no cms record yet). */
function partyTip(kind: 'billto' | 'customer', id: number | null | undefined, node: React.ReactNode) {
  if (!id) return <>{node}</>;
  return <Tooltip placement="auto" content={<PartyAddressCard kind={kind} id={id} />}>{node}</Tooltip>;
}

/* ── Derived group-level helpers. Each surfaces a group-level aggregate so the
 * on-screen value and the (now client-side) TanTable sort agree. ───────────── */

/** The shared CSR across a group's lanes — the single value when every lane
 *  agrees (ignoring blanks), else "Multiple". '—' when no lane has a CSR. */
export function groupCsr(lanes: QuoteListRow[]): string {
  const set = new Set(lanes.map((l) => (l.csr || '').trim()).filter(Boolean));
  if (set.size === 0) return '—';
  if (set.size === 1) return [...set][0];
  return 'Multiple';
}

/** Shared key-account across a group's lanes (a bill-to-level attribute, so it's
 *  the same on every lane); '' when none. */
export function groupKeyAccount(lanes: QuoteListRow[]): string {
  const set = new Set(lanes.map((l) => (l.keyAccount || '').trim()).filter(Boolean));
  if (set.size === 0) return '';
  if (set.size === 1) return [...set][0];
  return 'Multiple';
}

/** The group's Sage tag (cms.billtos.mas90code) — a bill-to attribute, so it's the
 *  same across a group's lanes; '' when none. */
export function groupSageTag(lanes: QuoteListRow[]): string {
  const set = new Set(lanes.map((l) => (l.sageTag || '').trim()).filter(Boolean));
  if (set.size === 0) return '';
  if (set.size === 1) return [...set][0];
  return 'Multiple';
}

/** Status roll-up: [status, count] pairs across the group's lanes. */
export function groupStatusCounts(lanes: QuoteListRow[]): [QuoteStatus, number][] {
  const m = new Map<QuoteStatus, number>();
  for (const l of lanes) m.set(l.status, (m.get(l.status) ?? 0) + 1);
  return [...m.entries()];
}

/** Tie-break priority for the dominant-status roll-up (most-relevant first).
 *  Lower index = preferred when two statuses have the same lane count. */
const STATUS_PRIORITY: QuoteStatus[] = ['SECURED', 'UNSECURED', 'EXPIRED'];

/** Status badges for an account: the backend `statusCounts` (over ALL current-rev
 *  quotes incl. EXPIRED — which aren't loaded as lanes) when present, in canonical
 *  order; otherwise the loaded lanes' counts. This is what surfaces the EXPIRED
 *  count in the Accounts view. */
export function groupStatusBadges(g: QuoteGroup): [QuoteStatus, number][] {
  const sc = g.statusCounts;
  if (sc && Object.keys(sc).length > 0) {
    return STATUS_PRIORITY
      .filter((s) => (sc[s] ?? 0) > 0)
      .map((s) => [s, sc[s] as number]);
  }
  return groupStatusCounts(g.lanes);
}

/** A single, deterministic Status key for a group: the status with the MOST
 *  lanes; ties broken by STATUS_PRIORITY. '—' for an empty group. This is the
 *  value the Status column sorts/filters/searches on (the cell still renders the
 *  full per-status badge roll-up). */
export function groupDominantStatus(lanes: QuoteListRow[]): string {
  const counts = groupStatusCounts(lanes);
  if (counts.length === 0) return '—';
  return counts.reduce((best, cur) => {
    if (cur[1] !== best[1]) return cur[1] > best[1] ? cur : best;
    return STATUS_PRIORITY.indexOf(cur[0]) < STATUS_PRIORITY.indexOf(best[0]) ? cur : best;
  })[0];
}

interface Props {
  groups: QuoteGroup[];
  /** Total group count — ALL matching groups are loaded (the grid sorts /
   *  filters / searches / pages over the full set client-side). Drives the
   *  GROUPED header label only; the page range lives in TanTable's footer. */
  totalGroups: number;
  /** FLAT-mode data: every group's lanes flattened to one row per quote. The
   *  parent derives this from the SAME loaded grouped response (no extra fetch);
   *  the toggle is a pure presentation switch. */
  flatLanes?: QuoteListRow[];
  /** Active display mode (default 'grouped'); persistence lives on the parent. */
  viewMode?: QuotesViewMode;
  /** Toggle handler — the parent owns the state + localStorage persistence. */
  onViewModeChange?: (mode: QuotesViewMode) => void;
  /** FLAT view-icon handler — opens the editor drawer in place on the parent for
   *  the clicked quote (no navigation). Grouped view-icon still navigates. */
  onOpenQuote?: (lane: QuoteListRow) => void;
  loading?: boolean;
  error?: string | null;
  /** Server-side filter toggles, shown in the FLAT (Quotes) view's table header
   *  before the search box (hidden in the grouped Accounts view). */
  showEnded?: boolean;
  allRevisions?: boolean;
  /** "Include Future": also show future-dated (not-yet-current) revisions on top
   *  of the current ones. Disabled while `allRevisions` (All already has them). */
  includeFuture?: boolean;
  onShowEndedChange?: (checked: boolean) => void;
  onAllRevisionsChange?: (checked: boolean) => void;
  onIncludeFutureChange?: (checked: boolean) => void;
  /** "Active Only" (Accounts view): ON loads active accounts only, OFF loads all
   *  incl. fully-expired. Server-side (parent-controlled) — toggling refetches. */
  activeOnly?: boolean;
  onActiveOnlyChange?: (checked: boolean) => void;
  /** TEST-ONLY escape hatch (default `false`). PRODUCTION leaves it `false` so the
   *  grid renders a VIRTUALIZED scrolling list (perf). jsdom gives the scroll
   *  container 0 height, so the virtualizer renders 0 rows; the row-asserting
   *  tests instead install a dimension-mock shim (stub clientHeight/
   *  getBoundingClientRect) so the real virtualized path renders a window of rows.
   *  This prop is a forward-looking seam (forwarded straight to TanTable's
   *  `disableVirtualization`) — pass `true` to bypass virtualization entirely if a
   *  future test needs every row without the shim. No current test passes it. */
  disableVirtualization?: boolean;
  /** Flat-view dynamic columns: the supported, displayable scope='quotes' field
   *  defs offered in the column picker (parent already filters to single-valued,
   *  picker-eligible fields). Omit/empty to disable the picker. */
  quoteFieldDefs?: FieldDefinition[];
  /** Currently-added dynamic column field names (flat view). TanTable persists
   *  this and syncs the parent via onAdditionalFieldsChange on mount. */
  additionalFields?: string[];
  /** Called when the user adds/removes a dynamic column; the parent updates state
   *  and refetches so the new field's data is included. */
  onAdditionalFieldsChange?: (fields: string[]) => void;
  /** FLAT view only: whether the persisted dynamic-column list (additionalFields)
   *  has finished loading. The flat grid persists its layout (order/width/
   *  visibility) under `table_quotes-flat-grid`, but the dynamic columns load from
   *  a SEPARATE store (`quotes_flat_columns`). If the grid mounted before that list
   *  resolved, it would apply the saved layout while the dynamic columns don't yet
   *  exist — dropping their saved positions and then autosaving the degraded layout
   *  (wiping the user's setup on a cold browser). Hold the mount until it's ready.
   *  Defaults true so existing callers/tests are unaffected. */
  flatFieldsReady?: boolean;
}

/**
 * Quotes — Results grid, rendered with the shared TanTable. An Accounts|Quotes
 * toggle (in TanTable's headerExtra slot) switches between two presentations of
 * the SAME loaded data — no refetch:
 *   • GROUPED (default): one row per (customer, bill-to) group; clicking the
 *     row (or the Customer link) NAVIGATES to the group detail page (below).
 *   • FLAT: one row per quote (every group's `lanes` flattened by the parent);
 *     clicking the row (or the Quote # link) opens the editor drawer IN PLACE
 *     on the quotes page (onOpenQuote) — no navigation.
 *
 * GROUPED detail (below): clicking a group row NAVIGATES to the dedicated
 * grouped detail page (/order-management/quotes/group/:customercode/:billto),
 * which shows that group's lanes. The Customer-name link does the same; it
 * stops propagation so it doesn't double-fire the row handler.
 *
 * The parent loads EVERY matching group in one request, so the grid runs in
 * FULL client-side mode: TanTable does the sorting (every data column sortable),
 * the in-grid column filters, the global search box, AND the pagination (its
 * built-in rows-per-page footer). Export uses TanTable's built-in Excel export,
 * which emits the on-screen GROUP columns (Customer, Customer Code, Bill-To,
 * CSR, # Quotes, Status) — one row per group.
 *
 * Sort keys for DERIVED columns (so the visible value and the sort agree):
 *   • Customer / Customer Code / Bill-To — the displayed value (Bill-To sorts on
 *     billToName; "(NEW BILL TO)" groups, billToNumber===0, sort together by
 *     empty name).
 *   • CSR — the shared `groupCsr` string ("Multiple" sorts alphabetically).
 *   • # Quotes — numeric `laneCount`.
 *   • Status — the group's DOMINANT status (most lanes; ties by STATUS_PRIORITY,
 *     active-first), sorted alphabetically. Deterministic, not "perfect".
 *
 * Row selection is OFF (selection lives on the detail page).
 */
const QuotesTable: React.FC<Props> = ({
  groups,
  totalGroups,
  flatLanes = [],
  viewMode = 'grouped',
  onViewModeChange,
  onOpenQuote,
  loading,
  error,
  showEnded,
  allRevisions,
  includeFuture,
  onShowEndedChange,
  onAllRevisionsChange,
  onIncludeFutureChange,
  activeOnly = false,
  onActiveOnlyChange,
  disableVirtualization = false,
  quoteFieldDefs = [],
  additionalFields = [],
  onAdditionalFieldsChange,
  flatFieldsReady = true,
}) => {
  const navigate = useNavigate();
  // Marks a quote viewed when its row is opened; the flat view's Quote # column
  // shows the green pill + last-opened ring. No-op without a ViewedProsProvider.
  const { markViewed } = useViewedPros();

  // Clicking the Tools view icon opens the group's dedicated detail page (which
  // shows the lanes); the group object rides along in router state so the
  // detail page renders without a refetch. customercode may be null → "null" in
  // the path (the detail page maps a non-numeric param back to null on a cold
  // deep-link).
  const openGroup = React.useCallback((g: QuoteGroup) => {
    navigate(
      `/order-management/quotes/group/${g.customercode ?? 'null'}/${g.billToNumber}`,
      { state: { group: g } },
    );
  }, [navigate]);

  // ── Column defs (left → right). EVERY data column is sortable (client-side);
  // only Tools is not. Each derived cell surfaces the SAME aggregate its
  // accessor sorts/filters on. ──────────────────────────────────────────────
  const columns = useMemo<ColumnDef<QuoteGroup>[]>(() => [
    {
      id: 'customer',
      accessorFn: (g) => g.customerName,
      header: 'Customer',
      // Our address tooltip already shows the full name — suppress TanTable's
      // truncation tooltip so a truncated cell doesn't show two tooltips.
      meta: { disableTruncationTooltip: true },
      // Plain text — the whole row opens the account detail page (onRowClick).
      cell: ({ row }) => partyTip('customer', row.original.customercode,
        <span className="font-semibold text-black dark:text-slate-100">
          {row.original.customerName}{recnoTag(row.original.customercode)}{newCustomerTag(row.original.customercode)}
        </span>
      ),
    },
    {
      id: 'billTo',
      // Sort/filter on the bill-to NAME; "(NEW BILL TO)" groups (billToNumber===0)
      // carry an empty name and so group together at one end.
      accessorFn: (g) => g.billToName,
      header: 'Bill-To',
      meta: { disableTruncationTooltip: true },   // address tooltip shows the full name
      cell: ({ row }) => {
        const g = row.original;
        return g.billToNumber === 0 ? (
          <span className="font-semibold text-amber-700 dark:text-amber-400 tracking-wide">(NEW BILL TO)</span>
        ) : partyTip('billto', g.billToNumber,
          <span>{g.billToName || '—'}{recnoTag(g.billToNumber)}</span>
        );
      },
    },
    {
      id: 'sageTag',
      // Bill-to Sage tag (mas90code) — same across the group's lanes.
      accessorFn: (g) => groupSageTag(g.lanes),
      header: 'Sage Tag',
      cell: ({ row }) => {
        const s = groupSageTag(row.original.lanes);
        return s ? <span>{s}</span> : <span className="text-slate-400 dark:text-slate-500">—</span>;
      },
    },
    {
      id: 'keyAccount',
      accessorFn: (g) => groupKeyAccount(g.lanes),
      header: 'Key Account',
      cell: ({ row }) => {
        const ka = groupKeyAccount(row.original.lanes);
        if (!ka) return <span className="text-slate-400 dark:text-slate-500">—</span>;
        const cls = keyAccountClass(ka);
        return cls
          ? <span className={`inline-flex items-center px-2 py-px text-[0.6875rem] font-semibold rounded ${cls}`}>{ka}</span>
          : <span>{ka}</span>;
      },
    },
    {
      id: 'csr',
      accessorFn: (g) => groupCsr(g.lanes),
      header: 'CSR',
      cell: ({ row }) => <span>{groupCsr(row.original.lanes)}</span>,
    },
    {
      id: 'laneCount',
      accessorFn: (g) => g.laneCount,
      header: '# Quotes',
      size: 80,
      cell: ({ row }) => (
        <span className="font-semibold text-slate-700 dark:text-slate-200">{row.original.laneCount}</span>
      ),
    },
    {
      id: 'status',
      // Single deterministic roll-up key (dominant status) drives the column's
      // sort, dropdown filter and global search; the cell still renders the full
      // per-status badge breakdown.
      accessorFn: (g) => groupDominantStatus(g.lanes),
      header: 'Status',
      cell: ({ row }) => (
        <span className="flex items-center gap-1 flex-wrap">
          {groupStatusBadges(row.original).map(([s, n]) => (
            <span
              key={s}
              className={[
                'inline-block px-1.5 py-px text-[0.625rem] font-bold tracking-wider rounded border',
                statusBadgeClass(s),
              ].join(' ')}
            >
              {s} {n}
            </span>
          ))}
        </span>
      ),
    },
    // Year-over-year (cms.intelligence_report) per the group's bill-to. Margins
    // visible; volume/revenue optional (hidden by default — see
    // defaultColumnVisibility). "—" when no intelligence row / costing-redacted.
    {
      id: 'prevYrMargin',
      accessorFn: (g) => g.yoy?.prevYear.margin ?? null,
      header: 'Prior Yr Margin %',
      cell: ({ row }) => <span>{fmtYoyMargin(row.original.yoy?.prevYear.margin)}</span>,
    },
    {
      id: 'curYrMargin',
      accessorFn: (g) => g.yoy?.currentYear.margin ?? null,
      header: 'Current Yr Margin %',
      cell: ({ row }) => <span>{fmtYoyMargin(row.original.yoy?.currentYear.margin)}</span>,
    },
    {
      id: 'prevYrVolume',
      accessorFn: (g) => g.yoy?.prevYear.volume ?? null,
      header: 'Prior Yr Volume',
      cell: ({ row }) => <span>{fmtYoyVolume(row.original.yoy?.prevYear.volume)}</span>,
    },
    {
      id: 'curYrVolume',
      accessorFn: (g) => g.yoy?.currentYear.volume ?? null,
      header: 'Current Yr Volume',
      cell: ({ row }) => <span>{fmtYoyVolume(row.original.yoy?.currentYear.volume)}</span>,
    },
    {
      id: 'prevYrRevenue',
      accessorFn: (g) => g.yoy?.prevYear.revenue ?? null,
      header: 'Prior Yr Revenue',
      cell: ({ row }) => <span>{formatTotal(row.original.yoy?.prevYear.revenue ?? null)}</span>,
    },
    {
      id: 'curYrRevenue',
      accessorFn: (g) => g.yoy?.currentYear.revenue ?? null,
      header: 'Current Yr Revenue',
      cell: ({ row }) => <span>{formatTotal(row.original.yoy?.currentYear.revenue ?? null)}</span>,
    },
    {
      // Hidden by default, but globally searchable: lets the table search box find
      // the account that contains a given quote number (each lane's quoteId, e.g.
      // "113403-1", contains its number). Show it (column picker) to list a group's
      // quotes inline.
      id: 'quoteNumbers',
      accessorFn: (g) => g.lanes.map((l) => l.quoteId).join(' '),
      header: 'Quote #s',
      enableGlobalFilter: true,
      cell: ({ row }) => <span className="text-slate-600 dark:text-slate-300">{row.original.lanes.map((l) => l.quoteId).join(', ')}</span>,
    },
  ], []);

  // Per-column filter type: low-cardinality roll-ups (CSR, Status) get a
  // dropdown (faceted on their single accessor value); everything else is a
  // free-text contains filter (high cardinality / dates).
  const columnFilterTypes = useMemo<Record<string, 'dropdown' | 'text'>>(() => ({
    customer: 'text',
    billTo: 'text',
    keyAccount: 'dropdown',
    csr: 'dropdown',
    laneCount: 'text',
    status: 'dropdown',
    prevYrMargin: 'text',
    curYrMargin: 'text',
    prevYrVolume: 'text',
    curYrVolume: 'text',
    prevYrRevenue: 'text',
    curYrRevenue: 'text',
  }), []);

  // ── FLAT mode ───────────────────────────────────────────────────────────────
  // One row per quote (each group's lanes flattened). Clicking the row (or the
  // Quote # link) opens the editor drawer IN PLACE (onOpenQuote) rather than
  // navigating. Reuses laneSummary (FSC/Total) + statusBadgeClass.
  const flatColumns = useMemo<ColumnDef<QuoteListRow>[]>(() => [
    {
      id: 'quoteId',
      accessorFn: (l) => l.quoteId,
      header: 'Quote #',
      // Plain text — the whole row opens the editor drawer in place (onRowClick).
      cell: ({ row }) => (
        <span className="font-semibold text-black dark:text-slate-100">{row.original.quoteId}</span>
      ),
    },
    {
      id: 'customer',
      accessorFn: (l) => l.customer,
      header: 'Customer',
      meta: { disableTruncationTooltip: true },   // address tooltip shows the full name
      cell: ({ row }) => partyTip('customer', row.original.customerCode,
        <span>{row.original.customer || '—'}{recnoTag(row.original.customerCode)}{newCustomerTag(row.original.customerCode)}</span>
      ),
    },
    {
      id: 'billTo',
      // "(NEW BILL TO)" deferred bill-to (billToNumber===0); sort/filter on the
      // displayed bill-to name otherwise.
      accessorFn: (l) => (l.billToNumber === 0 ? '' : l.billTo),
      header: 'Bill-To',
      meta: { disableTruncationTooltip: true },   // address tooltip shows the full name
      cell: ({ row }) => {
        const l = row.original;
        return l.billToNumber === 0 ? (
          <span className="font-semibold text-amber-700 dark:text-amber-400 tracking-wide">(NEW BILL TO)</span>
        ) : partyTip('billto', l.billToNumber,
          <span>{l.billTo || '—'}{recnoTag(l.billToNumber)}</span>
        );
      },
    },
    {
      id: 'lane',
      accessorFn: (l) => l.lane,
      header: 'Lane',
      cell: ({ row }) => (
        <span className="font-medium text-black dark:text-slate-100">{row.original.lane || '—'}</span>
      ),
    },
    {
      id: 'equipMove',
      accessorFn: (l) => equipMove(l),
      header: 'Equip · Move',
      cell: ({ row }) => <span>{equipMove(row.original) || '—'}</span>,
    },
    {
      id: 'dray',
      accessorFn: (l) => l.drayRate ?? '',
      header: 'Dray',
      cell: ({ row }) => <span>{formatDray(row.original.drayRate)}</span>,
    },
    {
      id: 'total',
      // Sort on the numeric all-in total; blanks (no dray base) sort last and show
      // as "(Blank)" in the filter — not a -Infinity sentinel. Cell formats "$1,037.00".
      accessorFn: (l) => laneAllInTotal(l) ?? undefined,
      sortUndefined: 'last',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-semibold text-black dark:text-slate-100">{formatTotal(laneAllInTotal(row.original))}</span>
      ),
    },
    {
      id: 'margin',
      // Per-quote ACTUAL margin% (intelligence_reportquotes); sort numeric, blanks
      // last and shown as "(Blank)" in the filter (not a -Infinity sentinel).
      accessorFn: (l) => l.actualMargin ?? undefined,
      sortUndefined: 'last',
      header: 'Margin %',
      cell: ({ row }) => (
        <span>{row.original.actualMargin == null ? '—' : `${row.original.actualMargin.toFixed(2)}%`}</span>
      ),
    },
    {
      id: 'status',
      accessorFn: (l) => l.status,
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={[
            'inline-block px-2 py-[2px] text-[0.625rem] font-bold uppercase tracking-wider rounded border',
            statusBadgeClass(row.original.status),
          ].join(' ')}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'imported',
      // Optional (default-hidden) column: v2-migration origin + verified state.
      // accessorFn returns a filterable label ('Verified' / 'Imported' / '').
      accessorFn: (l) => (l.v2QuoteNumber ? (l.v2MigrationVerified ? 'Verified' : 'Imported') : ''),
      header: 'Imported',
      cell: ({ row }) => {
        const l = row.original;
        if (!l.v2QuoteNumber) return <span className="text-slate-300 dark:text-slate-600">—</span>;
        const title = `Migrated from CMSv2 (quote ${l.v2QuoteNumber}${l.v2Lane ? `-${l.v2Lane}` : ''}) · ${l.v2MigrationVerified ? 'verified' : 'not yet verified'}`;
        const cls = l.v2MigrationVerified
          ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700'
          : 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
        return (
          <span title={title} className={`inline-block px-1.5 py-[2px] text-[0.625rem] font-bold uppercase tracking-wider rounded border ${cls}`}>
            {l.v2MigrationVerified ? '✓ V2' : 'V2'}
          </span>
        );
      },
    },
    {
      id: 'csr',
      accessorFn: (l) => l.csr || '',
      header: 'CSR',
      cell: ({ row }) => <span>{row.original.csr || '—'}</span>,
    },
    {
      id: 'soldBy',
      accessorFn: (l) => l.soldBy || '',
      header: 'Sold By',
      cell: ({ row }) => <span>{row.original.soldBy || '—'}</span>,
    },
    {
      id: 'createDate',
      accessorFn: (l) => l.createdDate || '',
      header: 'Create Date',
      cell: ({ row }) => <span>{row.original.createdDate || '—'}</span>,
    },
  ], []);

  // ── Flat-view dynamic columns ──────────────────────────────────────────────
  // The column picker offers any supported scope='quotes' field (passed in by the
  // parent); each selected one becomes a generated column whose accessorKey is the
  // field name — matching the backend row key (rides in the grouped query's lanes).
  // generateColumnDef gives type-appropriate header/formatting, reused from the
  // dispatch-dashboard dynamic-column system.
  const quoteDefByName = useMemo(
    () => new Map(quoteFieldDefs.map((d) => [d.fieldname, d])),
    [quoteFieldDefs],
  );
  const availableFields = useMemo(
    () => quoteFieldDefs.map((d) => ({
      fieldname: d.fieldname,
      displayname: d.displayname,
      abbreviation: d.abbreviation,
      type: d.type,
    })),
    [quoteFieldDefs],
  );
  const flatColumnsWithExtra = useMemo<ColumnDef<QuoteListRow>[]>(() => {
    const extra = additionalFields
      .map((fn) => quoteDefByName.get(fn))
      .filter((d): d is FieldDefinition => !!d)
      .map((d) => generateColumnDef(d) as ColumnDef<QuoteListRow>);
    return extra.length ? [...flatColumns, ...extra] : flatColumns;
  }, [flatColumns, additionalFields, quoteDefByName]);

  // Flat per-column filter types: low-cardinality columns (Status, CSR, Bill-To)
  // get a dropdown; high-cardinality / free-text / dates get the text contains
  // filter.
  const flatColumnFilterTypes = useMemo<Record<string, 'dropdown' | 'text'>>(() => ({
    quoteId: 'text',
    customer: 'text',
    billTo: 'dropdown',
    lane: 'text',
    equipMove: 'text',
    dray: 'text',
    fsc: 'text',
    total: 'text',
    status: 'dropdown',
    imported: 'dropdown',
    csr: 'dropdown',
    soldBy: 'dropdown',
    createDate: 'text',
  }), []);

  // All-groups mode CAPS the load to the backend's GROUP_ALL_CAP (500) groups
  // while still reporting the TRUE total, so the loaded group count can be LESS
  // than totalGroups. When that happens the load is TRUNCATED: the grid renders a
  // "refine your search" banner and the header is reconciled to be honest about
  // showing only the first N. (Inferred from the loaded count vs. the true total —
  // the same signal the backend's explicit `truncated` flag carries.)
  // "Active Only" (Accounts view) is now a SERVER-SIDE, parent-controlled filter
  // (loads active accounts only vs all incl. fully-expired) — there are ~9k total
  // accounts vs ~500 active and the load is capped at 500 groups, so a client-side
  // filter over the loaded set couldn't show the right accounts. The parent has
  // already narrowed the dataset, so the grid renders every loaded group.
  const groupsToShow = groups;

  // Truncation is judged on the LOADED set vs the true total (the cap), not the
  // active-only filtered subset.
  const loadedGroups = groups.length;
  const truncated = loadedGroups < totalGroups;

  // Client-filtered row count from TanTable (onFilteredRowsChange) so the header
  // can show "X of Y" when a column/global filter narrows the loaded set — matching
  // the base tables. null until first reported; reset when the loaded set changes
  // (keyed on lengths, so applying a column filter — which doesn't change the data
  // passed in — doesn't reset it).
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  useEffect(() => { setFilteredCount(null); }, [flatLanes.length, groupsToShow.length, viewMode]);
  const handleFilteredRowsChange = useCallback((rows: unknown[]) => setFilteredCount(rows.length), []);
  const fmtCount = (total: number, noun: string): string =>
    filteredCount != null && filteredCount < total
      ? `${filteredCount.toLocaleString()} of ${total.toLocaleString()} ${noun}`
      : `${total.toLocaleString()} ${noun}`;

  // ALL matching groups are loaded — the header carries the total (or "X of Y" when
  // a client filter narrows it); the grid body is a virtualized, scrolling list (no
  // pagination footer). ACCOUNTS view counts accounts (groups); QUOTES view counts
  // quotes (flattened lane rows). When truncated, the grouped header stays honest
  // about the cap ("showing 500 of N accounts").
  // "Active Only" filtering is server-side now, so totalGroups already reflects the
  // active/all choice — the title just reports loaded vs total (no activeOnly branch).
  const cardTitle =
    viewMode === 'flat'
      ? `Quotes — Results (${fmtCount(flatLanes.length, 'quotes')})`
      : truncated
        ? `Accounts — Results (showing ${loadedGroups.toLocaleString()} of ${totalGroups.toLocaleString()} accounts)`
        : `Accounts — Results (${fmtCount(totalGroups, 'accounts')})`;

  // Segmented Accounts | Quotes toggle (grouped/flat view modes), hosted in
  // TanTable's headerExtra slot (next to the title). role="group" + aria-pressed
  // on each option makes the active mode announceable; the parent owns the state
  // + localStorage persistence.
  const viewModeToggle = (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex items-center rounded-md border border-slate-500 overflow-hidden text-[0.6875rem] font-semibold"
    >
      {(['grouped', 'flat'] as const).map((mode) => {
        const active = viewMode === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={active}
            onClick={() => onViewModeChange?.(mode)}
            className={[
              'px-2.5 py-1 capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
              active
                ? 'bg-blue-600 text-white'
                : 'bg-slate-700 text-slate-200 hover:bg-slate-600',
            ].join(' ')}
          >
            {mode === 'grouped' ? 'Accounts' : 'Quotes'}
          </button>
        );
      })}
    </div>
  );

  // Server-side filter toggles (Show Ended / Current|All Revisions), shown in the
  // FLAT (Quotes) view's table header before the search box. Light text for the
  // dark TanTable header bar; hidden in the grouped Accounts view (not passed).
  const quotesFilterToggles = (
    <div className="flex items-center gap-3.5">
      <label className="flex items-center gap-1.5 text-[0.71875rem] font-medium text-slate-200 cursor-pointer whitespace-nowrap">
        <Toggle size="sm" checked={!!showEnded} onChange={onShowEndedChange} ariaLabel="Show ended quotes" />
        Show Ended
      </label>
      <Tooltip content={`Toggle To See ${allRevisions ? 'Current' : 'All'} Revisions`} placement="bottom">
        <label className="flex items-center gap-1.5 text-[0.71875rem] font-medium text-slate-200 cursor-pointer whitespace-nowrap">
          {/* Off (Current Revisions) is the normal state, so colour it green to
              read as "active" rather than a greyed-out off; On (All) stays default. */}
          <Toggle
            size="sm"
            checked={!!allRevisions}
            onChange={onAllRevisionsChange}
            ariaLabel="Show all revisions"
            className={allRevisions ? undefined : '!bg-green-500 dark:!bg-green-600'}
          />
          {allRevisions ? 'All Revisions' : 'Current Revisions'}
        </label>
      </Tooltip>
      {/* "Include Future": a revision whose Effective Date is after today is queued
          (not yet the current revision). ON adds those on top of the current
          ones. Disabled under All Revisions, which already contains them. */}
      <Tooltip
        content={allRevisions
          ? 'All Revisions already includes future-dated revisions'
          : 'Also show revisions whose Effective Date is after today (not yet active)'}
        placement="bottom"
      >
        <label
          className={[
            'flex items-center gap-1.5 text-[0.71875rem] font-medium text-slate-200 whitespace-nowrap',
            allRevisions ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          ].join(' ')}
        >
          <Toggle
            size="sm"
            checked={!!includeFuture}
            onChange={onIncludeFutureChange}
            ariaLabel="Include future revisions"
            disabled={!!allRevisions}
          />
          Include Future
        </label>
      </Tooltip>
    </div>
  );

  // "Active Only" toggle for the ACCOUNTS (grouped) view's header — server-side
  // (parent refetches): ON (default) loads active accounts only, OFF loads all
  // incl. fully-expired.
  const accountsFilterToggle = (
    <Tooltip content="On: active accounts only. Off: include accounts whose quotes are all expired." placement="bottom">
      <label className="flex items-center gap-1.5 text-[0.71875rem] font-medium text-slate-200 cursor-pointer whitespace-nowrap">
        <Toggle size="sm" checked={activeOnly} onChange={(v) => onActiveOnlyChange?.(v)} ariaLabel="Active accounts only" />
        Active Only
      </label>
    </Tooltip>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="quotes-results-region">
      {error && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-[0.78125rem] text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800"
        >
          <div className="font-semibold mb-0.5">Search failed</div>
          <div className="text-[0.6875rem] opacity-80">{error}</div>
        </div>
      )}

      {/* Truncation banner: the all-groups load is capped at the backend's
          GROUP_ALL_CAP (500) groups. When the loaded set is smaller than the true
          total, the grid is showing only the first slice — tell the user to narrow
          the search (the client-side sort/filter/search then operate on the loaded
          set). role="status" so screen readers announce it without interrupting;
          amber/info styling (noticeable, non-alarming). */}
      {truncated && (
        <div
          role="status"
          className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2.5 text-[0.78125rem] text-amber-800 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-700"
        >
          Showing the first {loadedGroups.toLocaleString()} of{' '}
          {totalGroups.toLocaleString()} customer groups — refine your search to
          narrow the results.
        </div>
      )}

      {/* The dynamic results title is the TanTable header itself: we pass the
          fully-formed label as `cardTitle` and set `hideTitleCount` so TanTable
          does NOT append its own `(rowCount)` after it. The Accounts|Quotes toggle
          rides in TanTable's `headerExtra` slot (next to the title). Both modes
          render through TanTable with the SAME full client-side features (sort /
          column-filter / global-search controls, density/fit/show-hide toolbar,
          built-in Excel export, client pagination footer) over the FULL loaded
          set — only the data + column defs + view-icon behavior differ. Row
          selection stays OFF in BOTH modes; last-synced shows in the
          footer-left slot.

          GROUPED: one row per (customer, bill-to) group; clicking the row
            NAVIGATES to the group detail page.
          FLAT: one row per quote (lanes flattened, NO refetch); clicking the row
            opens the editor drawer IN PLACE via onOpenQuote. */}
      {viewMode === 'flat' && !flatFieldsReady ? (
        // Hold the flat grid's mount until the persisted dynamic-column list has
        // loaded, so TanTable applies its saved layout with every column present
        // (see flatFieldsReady). A brief placeholder — the search itself is also
        // loading at this point on a cold start.
        <div
          data-testid="quotes-flat-loading"
          className="flex items-center justify-center py-16 text-[0.78125rem] text-slate-400 dark:text-slate-500"
        >
          Loading your columns…
        </div>
      ) : viewMode === 'flat' ? (
        // Distinct `key` per view is REQUIRED: the flat and grouped grids are both
        // <TanTable> in this same ternary slot, so without a key React reuses ONE
        // instance across the Accounts↔Quotes toggle and just swaps props —
        // including settingsKey (quotes-flat-grid ↔ quotes-grouped-grid). But
        // TanTable only loads its settings once per mount (its load effect
        // early-returns on settingsLoaded and never re-reads on a settingsKey
        // change), so the reused instance keeps the other view's layout and its
        // debounced autosave then writes that layout into the wrong key — wiping
        // the flat view's added columns/order on toggle. Keying by settingsKey
        // forces a fresh mount per view, so each loads + saves its own settings.
        <TanTable<QuoteListRow>
          key="quotes-flat-grid"
          data={flatLanes}
          columns={flatColumnsWithExtra}
          trackViewedColumnId="quoteId"
          cardTitle={cardTitle}
          headerExtra={viewModeToggle}
          searchPrefix={quotesFilterToggles}
          hideTitleCount
          onFilteredRowsChange={handleFilteredRowsChange}
          // Dynamic column picker (flat view only): offer the supported quote
          // fields; toggling one updates additionalFields, which the parent uses
          // to refetch so the field's data is present.
          availableFields={availableFields}
          additionalFields={additionalFields}
          onAdditionalFieldsChange={onAdditionalFieldsChange}
          // Whole row opens the editor drawer in place; mark the quote viewed so
          // its Quote # cell shows the green pill + last-opened ring.
          onRowClick={(l) => { markViewed(l.quoteId); onOpenQuote?.(l); }}
          getRowId={(l) => l.quoteId}
          loading={loading}
          settingsKey="quotes-flat-grid"
          // Each view now remounts on toggle (see the per-view key above), so apply
          // the localStorage-cached layout pre-paint to avoid flashing the default
          // column order/width before the async settings load resolves.
          seedLayoutFromCache
          // "Imported" (v2 origin) is optional — hidden by default, toggle via the
          // column menu.
          defaultColumnVisibility={{ imported: false }}
          disableVirtualization={disableVirtualization}
          enableColumnFilters
          columnFilterTypes={flatColumnFilterTypes}
          enableSearch
          enableShowHideCols
          enableExcelExport
          enableReset
          enableRowSelection={false}
        />
      ) : (
        // See the flat-view TanTable above: a distinct `key` keeps this grouped
        // instance separate so the two views never share state or cross-write
        // each other's saved layout (table_quotes-grouped-grid vs -flat-grid).
        <TanTable<QuoteGroup>
          key="quotes-grouped-grid"
          data={groupsToShow}
          columns={columns}
          cardTitle={cardTitle}
          headerExtra={viewModeToggle}
          searchPrefix={accountsFilterToggle}
          hideTitleCount
          onFilteredRowsChange={handleFilteredRowsChange}
          // Whole row navigates to the account (group) detail page (same as the
          // Customer link, which stops propagation so it doesn't double-fire).
          onRowClick={openGroup}
          // (customercode, billToNumber) is unique for normal billto>0 groups,
          // but the backend emits each deferred "(NEW BILL TO)" quote
          // (billToNumber===0) as its own standalone singleton group — so two
          // such groups for the SAME customer both stringify to "<code>-0" and
          // collide as React row keys. Append the first lane's quoteId (the
          // singleton's stable per-group discriminator; lanes arrive in a stable
          // backend order) to break that tie. Harmless for billto>0 groups
          // (already unique on (code, billTo)).
          getRowId={(g) => `${g.customercode ?? 'na'}-${g.billToNumber}-${g.lanes[0]?.quoteId ?? ''}`}
          loading={loading}
          settingsKey="quotes-grouped-grid"
          // Remounts on toggle (see the per-view key above); seed from cache pre-paint.
          seedLayoutFromCache
          // YoY volume/revenue are optional — hidden by default, toggle via the
          // column menu. Margins stay visible.
          defaultColumnVisibility={{ prevYrVolume: false, curYrVolume: false, prevYrRevenue: false, curYrRevenue: false, quoteNumbers: false }}
          disableVirtualization={disableVirtualization}
          enableColumnFilters
          columnFilterTypes={columnFilterTypes}
          enableSearch
          enableShowHideCols
          enableExcelExport
          enableReset
          enableRowSelection={false}
        />
      )}
    </div>
  );
};

export default QuotesTable;
